import {Component, OnInit} from '@angular/core';
import {AsyncPipe, DecimalPipe, NgClass, NgForOf, NgIf} from "@angular/common";
import {ReactiveFormsModule} from "@angular/forms";
import {SubscriptionBannerComponent} from "../../../common/subscription-banner/subscription-banner.component";
import { MatProgressBarModule } from '@angular/material/progress-bar';
import {selectPsyTestReport, selectPsyTestReportError} from "../../../state/psyTesting/psyTesting.selectors";
import {fetchPsyTestReport} from "../../../state/psyTesting/psyTesting.actions";
import {Store} from "@ngrx/store";
import {filter, Subscription, take} from "rxjs";
import {PopupService} from "../../../services/popup.service";
import {HeaderComponent} from "../../../common/header/header.component";
import {QuadrantChartComponent} from "../quadrant-chart/quadrant-chart.component";
import {HeaderService} from "../../../services/header.service";
import {AuthService} from "../../../services/auth.service";

@Component({
  selector: 'app-psy-testing-report',
  standalone: true,
  imports: [
    AsyncPipe,
    DecimalPipe,
    NgIf,
    ReactiveFormsModule,
    SubscriptionBannerComponent,
    MatProgressBarModule,
    NgForOf,
    NgClass,
    HeaderComponent,
    QuadrantChartComponent // Add this to imports
  ],
  templateUrl: './psy-testing-report.component.html',
  styleUrl: './psy-testing-report.component.scss'
})
export class PsyTestingReportComponent implements OnInit {

  empathicConcernProgress: number = 0;
  perspectiveTakingProgress: number = 0;

  emp: number = 0;
  comp: number = 0;

  // Values for quadrant chart (0-5 scale)
  xValue: number = 4;
  yValue: number = 4;
  personalityType: 'Popular' | 'Performer' | 'Procrastinator' | 'Pusher' = 'Pusher';

  testUser: any;
  user: any;

  constructor(
    private store: Store,
    private popupService: PopupService,
    private headerService: HeaderService,
    private authService: AuthService
  ) {

    this.headerService.setHeaderState('What’s your P as sales Person?', null, true);

  }

  ngOnInit() {
    // Get authenticated user first
    this.authService.authUser().subscribe(user => {
      this.user = user;
      this.fetchPsyTestReport();
    });
  }

  fetchPsyTestReport() {
    const code = this.user?.test_code;

    if (!code) {
      this.popupService.notify('Psy test code is missing. Please take the test first.', 'Error', 'error');
      return;
    }

    // Dispatch the action to fetch the psy test report
    this.store.dispatch(fetchPsyTestReport({ code }));

    // Select the report result and error from the store
    const psyTestReport$ = this.store.select(selectPsyTestReport);
    const psyTestReportError$ = this.store.select(selectPsyTestReportError);

    // Handle success
    psyTestReport$.pipe(
      filter((report) => Boolean(report)), // Ignore null or undefined
      take(1)
    ).subscribe({
      next: (report) => {
        console.log('Psy Test report fetched successfully:', report);

        if (report) {
          this.testUser = report.testUser;

          // Find the actual scores from the scales
          const empathicConcernScale = report.testUser.scales.find((scale: any) => scale.name === 'Empathic concern');
          const needToConquerScale = report.testUser.scales.find((scale: any) => scale.name === 'Need to conquer');

          // Use the actual scores if found, otherwise use the test values
          this.emp = empathicConcernScale ? empathicConcernScale.score : 22;
          this.comp = needToConquerScale ? needToConquerScale.score : 13;

          // Use score from API directly for x,y coordinates
          const score = report.testUser.score || 1;
          this.xValue = score;
          this.yValue = score;

          // Ensure values are within 0-5 range
          this.xValue = Math.max(0, Math.min(5, this.xValue));
          this.yValue = Math.max(0, Math.min(5, this.yValue));

          // Use personality type from API
          const userType = report.testUser.user_type;
          switch (userType) {
            case 'The Popular':
              this.personalityType = 'Popular';
              break;
            case 'The Performer':
              this.personalityType = 'Performer';
              break;
            case 'The Procrastinator':
              this.personalityType = 'Procrastinator';
              break;
            case 'The Pusher':
              this.personalityType = 'Pusher';
              break;
            default:
              // Fallback to calculation if user_type is not recognized
              if (this.emp >= 21 && this.comp < 12) {
                this.personalityType = 'Popular';
              } else if (this.emp >= 21 && this.comp >= 12) {
                this.personalityType = 'Performer';
              } else if (this.emp < 21 && this.comp < 12) {
                this.personalityType = 'Procrastinator';
              } else {
                this.personalityType = 'Pusher';
              }
          }
        }

        this.popupService.notify('Psy Test report fetched successfully!', 'Success', 'success');
      },
      error: (error) => {
        console.error('Psy Test report subscription error:', error);
      }
    });

    // Handle failure
    psyTestReportError$.pipe(
      filter((error) => Boolean(error)), // Ignore null or undefined
      take(1)
    ).subscribe({
      next: (errorMessage) => {
        this.popupService.notify(errorMessage, 'Error', 'error');
      },
      error: (error) => {
        console.error('Psy Test report error subscription error:', error);
      }
    });
  }
}
